import {
    BatchRequestContent,
    BatchRequestStep,
    BatchResponseContent,
    Client,
    ResponseType,
} from "@microsoft/microsoft-graph-client/lib/src/browser";
import { ITeam } from "../interfaces/services/ITeam";
import { logger } from "../../../utilities/logger";

export interface ITeamSiteResolution {
    siteId: string;
    webUrl: string;
    driveId: string;
}

export interface ITeamSiteResolveResult {
    /** Teams with a definitive outcome: a real site resolution, or the shared-team fallback (403/404). */
    resolved: ITeam[];
    /** Teams still throttled after every retry round. Callers must drop these - never substitute a siteId. */
    unresolved: ITeam[];
}

export interface ITeamSiteResolveOptions {
    concurrency?: number;
    maxRounds?: number;
    delayFn?: (ms: number) => Promise<void>;
}

// Graph rejects more than 20 requests per $batch.
const BATCH_SIZE = 20;
// Each inner request counts individually against SharePoint's throttling quotas, so wider
// fan-out saves nothing quota-wise and starts to look like the request spikes SharePoint
// answers with dynamic (503) throttling. Four batches in flight = at most 80 inner reads.
const DEFAULT_CONCURRENCY = 4;
const DEFAULT_MAX_ROUNDS = 3;
const MAX_BACKOFF_MS = 60000;
// SharePoint returns RateLimit headers (best effort) once >= 80% of the app's per-tenant
// 1-minute resource-unit budget is spent. One batch of 20 site reads costs >= 20 units,
// so when the remaining budget drops below this floor we stop fanning out pre-emptively
// instead of triggering the 429 ourselves.
const RATE_LIMIT_DOWNSHIFT_FLOOR = 100;

const defaultDelay = (ms: number): Promise<void> => new Promise(resolve => setTimeout(resolve, ms));

// 429 = quota exceeded, 5xx (most commonly 503 Server Busy) = transient service pressure.
// Both must be retried and must never take the shared-team fallback.
const isThrottleStatus = (status: number): boolean => status === 429 || status >= 500;

const parseRetryAfterSeconds = (headers: { get(name: string): string | null }): number => {
    try {
        const retryAfterSeconds = parseInt(headers?.get("Retry-After") ?? "", 10);
        return isNaN(retryAfterSeconds) ? 0 : retryAfterSeconds;
    } catch {
        return 0;
    }
};

/**
 * Resolves each team's backing SharePoint site (siteId/webUrl/driveId) via batched
 * `/groups/{id}/sites/root` reads, following the SharePoint throttling contract:
 *
 * - Bounded concurrency instead of sequential batches or an unbounded fan-out.
 * - Inner batch responses are inspected individually: throttled entries (429/5xx) are
 *   retried in a later round, only definitive failures (403/404 - typically a
 *   shared-channel team whose group lives in another tenant) take the shared-team
 *   fallback the rest of the app relies on.
 * - On any throttle signal the whole pool backs off globally for the longest
 *   Retry-After seen (throttled requests still count toward SharePoint quotas, so
 *   pressing on makes recovery slower), then continues sequentially.
 * - A definitive outer failure (4xx other than 429, e.g. an expired token - the client
 *   wraps a static token, so a 401 cannot heal within this run) aborts all rounds
 *   immediately instead of burning quota on replays that cannot succeed.
 * - Teams still throttled after the retry budget are returned as `unresolved` so the
 *   caller can exclude them; fabricating a siteId would poison caches downstream.
 */
export const resolveTeamSites = async (client: Client, teams: ITeam[], options?: ITeamSiteResolveOptions): Promise<ITeamSiteResolveResult> => {
    const concurrency = options?.concurrency ?? DEFAULT_CONCURRENCY;
    const maxRounds = options?.maxRounds ?? DEFAULT_MAX_ROUNDS;
    const delay = options?.delayFn ?? defaultDelay;

    const resolved: ITeam[] = [];
    let pending: ITeam[] = teams;
    let retryAfterSeconds = 0;
    let downshifted = false;
    let fatalAbort = false;

    for (let round = 0; round < maxRounds && pending.length > 0 && !fatalAbort; round++) {
        if (round > 0) {
            await delay(Math.min(Math.max(retryAfterSeconds * 1000, 2 ** round * 1000), MAX_BACKOFF_MS));
        }
        retryAfterSeconds = 0;

        const chunks: ITeam[][] = [];
        for (let i = 0; i < pending.length; i += BATCH_SIZE) {
            chunks.push(pending.slice(i, i + BATCH_SIZE));
        }

        const retry: ITeam[] = [];
        let abortRound = false;
        let nextChunk = 0;

        const processChunk = async (teamSlice: ITeam[]) => {
            const batchRequests: BatchRequestStep[] = teamSlice.map(team => {
                return {
                    id: team.id,
                    request: new Request(`/groups/${team.id}/sites/root?$select=id,webUrl&$expand=drive($select=id)`, {
                        method: "GET"
                    })
                };
            });

            let rawResponse: Response;
            try {
                const content = await new BatchRequestContent(batchRequests).getContent();
                rawResponse = await client.api("/$batch").responseType(ResponseType.RAW).post(content);
            } catch (batchError) {
                // Nothing in this slice got a definitive answer, so the whole slice is retried.
                retry.push(...teamSlice);
                const statusCode = (batchError as any)?.statusCode;
                if (isThrottleStatus(statusCode)) {
                    abortRound = true;
                }
                else if (typeof statusCode === "number" && statusCode >= 400) {
                    // A definitive HTTP failure (dead token, malformed request) replays
                    // identically - fail fast rather than burn quota on doomed retries.
                    fatalAbort = true;
                }
                // No statusCode means no HTTP response (transport blip): plain retry.
                logger(batchError, "TeamSiteResolver.ts (processChunk)");
                return;
            }

            if (!rawResponse?.ok) {
                retry.push(...teamSlice);
                if (isThrottleStatus(rawResponse?.status)) {
                    abortRound = true;
                    retryAfterSeconds = Math.max(retryAfterSeconds, parseRetryAfterSeconds(rawResponse.headers));
                }
                else {
                    fatalAbort = true;
                }
                return;
            }

            const rateLimitRemaining = parseInt(rawResponse.headers?.get("RateLimit-Remaining") ?? "", 10);
            if (!isNaN(rateLimitRemaining) && rateLimitRemaining < RATE_LIMIT_DOWNSHIFT_FLOOR) {
                downshifted = true;
            }

            let batchResponseContent: BatchResponseContent;
            try {
                batchResponseContent = new BatchResponseContent(await rawResponse.json());
            } catch (bodyParseError) {
                // No team in this slice has an outcome recorded yet, so a whole-slice
                // retry cannot duplicate results.
                retry.push(...teamSlice);
                logger(bodyParseError, "TeamSiteResolver.ts (processChunk)");
                return;
            }

            for (const team of teamSlice) {
                const teamSiteResponse = batchResponseContent.getResponseById(team.id);
                if (!teamSiteResponse) {
                    retry.push(team);
                }
                else if (teamSiteResponse.ok) {
                    try {
                        const teamSite = await teamSiteResponse.json();
                        resolved.push({ ...team, siteId: teamSite.id, webUrl: teamSite.webUrl, driveId: teamSite.drive?.id });
                    } catch (teamParseError) {
                        // Retry only this team: slice-mates already have their outcome
                        // recorded, and re-running them would duplicate entries.
                        retry.push(team);
                    }
                }
                else if (isThrottleStatus(teamSiteResponse.status)) {
                    retry.push(team);
                    retryAfterSeconds = Math.max(retryAfterSeconds, parseRetryAfterSeconds(teamSiteResponse.headers));
                    abortRound = true;
                }
                else {
                    // Definitive rejection (403/404): the group's root site is not reachable
                    // from this tenant, which is the shared-channel team shape.
                    resolved.push({ ...team, siteId: team.id, isSharedTeam: true });
                }
            }
        };

        const workerCount = Math.min(concurrency, chunks.length);
        await Promise.all(Array.from({ length: workerCount }, (_, workerIndex) =>
            (async () => {
                while (nextChunk < chunks.length && !abortRound && !fatalAbort) {
                    if (downshifted && workerIndex > 0) {
                        break;
                    }
                    await processChunk(chunks[nextChunk++]);
                }
            })()
        ));

        // Chunks never sent because the round stopped early are carried forward: into
        // the next round after an abort, or into `unresolved` after a fatal failure.
        while (nextChunk < chunks.length) {
            retry.push(...chunks[nextChunk++]);
        }

        if (abortRound) {
            downshifted = true;
        }
        pending = retry;
    }

    return { resolved, unresolved: pending };
};
